<template>
  <v-form ref="form" v-model="valid" class="px-4 py-2 pt-6 search-form">
    <v-row class="search">
      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-text-field
          v-model="submissionId"
          :label="$t('components.claims_history.search_by_id')"
          name="submission_id"
          dense
          append-icon="mdi-magnify"
          clearable
          :rules="[rules.claimSubmissionId]"
          @input="onGlobalSearch"
        ></v-text-field>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-text-field
          v-model="patient"
          :label="$t('components.claims_history.header.patient')"
          name="patient"
          dense
          append-icon="mdi-magnify"
          clearable
          :rules="[rules.patient]"
          @input="onGlobalSearch"
        ></v-text-field>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-text-field
          v-model="memberCertificate"
          :label="$t('enums.MemberCertificate')"
          name="member-certificate"
          dense
          append-icon="mdi-magnify"
          clearable
          :rules="[rules.memberCertificate]"
          @input="onGlobalSearch"
        ></v-text-field>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <trans-enum
          :value.sync="userType"
          :items="options.Enums.SubmissionSource"
          :label="$t('components.claims_history.claim_submission_type')"
          dense
          clearable
          @input="onGlobalSearch"
        ></trans-enum>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="pt-0 pt-2">
        <v-autocomplete
          ref="coverageAutocomplete"
          :loading="coverageLoading"
          dense
          v-model="selectedCoverage"
          :items="coverageItemsWithSelected"
          :label="$t('components.claims_history.coverage_benefit')"
          @keyup="onCoverageSearch"
          :search-input="coverageSearch"
          placeholder="Search by code or name"
          clearable
          :menu-props="{ contentClass: 'coverage-autocomplete-menu' }"
          multiple
          item-text="Name"
          item-value="Id"
          return-object
        ></v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-autocomplete
          ref="providerAutocomplete"
          :loading="providerLoading"
          dense
          v-model="selectedProviders"
          :items="providerItemsWithSelected"
          :label="$t('components.claims_history.provider')"
          @keyup="onProviderSearch"
          :search-input="providerSearch"
          placeholder="Search by code or name"
          clearable
          :menu-props="{ contentClass: 'provider-autocomplete-menu' }"
          multiple
          :item-text="
            (o) =>
              `${o.UniqueId} - ${o.FirstName} ${o.LastName ? o.LastName : ''}`
          "
          item-value="Id"
          return-object
          @input="providerInput"
          @change="providerChange"
        ></v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-autocomplete
          v-model="selectedStatus"
          :menu-props="{ contentClass: 'text-left' }"
          :label="$t('globals.status')"
          name="status"
          :items="statuses"
          dense
          @input="onGlobalSearch"
          clearable
          multiple
        >
          <v-list-item slot="prepend-item" ripple @click="toggle">
            <v-list-item-action>
              <v-icon>
                {{ icon }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ $t("components.trans-multi-select2.select_all") }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider slot="prepend-item" class="mt-2" />
          <template v-slot:selection="data">
            <span v-if="data.index === 0 && selectedStatus.length">
              {{ selectedStatus.map((item) => item).join(", ") }}
            </span>
          </template>
          <template v-slot:item="data">
            <v-list-item v-bind="data.attrs" v-on="data.on">
              <v-list-item-action>
                <v-checkbox v-model="data.attrs.inputValue"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ data.item.text }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="pt-0 pt-2">
        <v-menu
          v-model="showDatepicker"
          :nudge-right="40"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          left
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeText"
              :label="$t('components.claims_history.date_of_expense_range')"
              name="expenseDate"
              append-icon="mdi-calendar"
              dense
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
              @click:clear="clearDates"
              @click:append="showDatepicker = true"
            ></v-text-field>
          </template>
          <v-date-picker
            color="primary"
            @input="onDateOfExpenseSearch"
            no-title
            v-model="dates"
            :max="$helpers.toISOStringLocal(new Date())"
            range
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-row no-gutters v-if="selectAmountRange">
          <v-col class="pr-2">
            <v-range-slider
              color="primary"
              thumb-label="always"
              thumb-size="26"
              v-model="range"
              :max="max"
              :min="min"
              class="pt-3"
              @input="onGlobalSearch"
            />
          </v-col>
          <v-col cols="auto" class="px-0 pt-2">
            <v-icon small @click="disableRangeSelect">mdi-close</v-icon>
          </v-col>
        </v-row>
        <v-row no-gutters v-else>
          <v-col class="pr-0">
            <currency-textfield
              name="claimedAmount"
              prepend-inner-icon="mdi-currency-usd"
              dense
              v-model="claimedAmount"
              :label="$t('components.claims_history.header.claimed_total')"
              placeholder="0.00"
              class="money"
              clearable
              @input="onGlobalSearch"
            />
          </v-col>
          <v-col cols="auto" class="px-0 pt-4">
            <v-icon @click="selectAmountRange = true">
              mdi-dots-horizontal
            </v-icon>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-row no-gutters v-if="selectTotalPaidRange">
          <v-col class="pr-2">
            <v-range-slider
              color="primary"
              thumb-label="always"
              thumb-size="26"
              v-model="totalPaidRange"
              :max="max"
              :min="min"
              class="pt-3"
              @input="onGlobalSearch"
            />
          </v-col>
          <v-col cols="auto" class="px-0 pt-2">
            <v-icon small @click="disableTotalPaidRangeSelect">mdi-close</v-icon>
          </v-col>
        </v-row>
        <v-row no-gutters v-else>
          <v-col class="pr-0">
            <currency-textfield
              name="totalPaid"
              prepend-inner-icon="mdi-currency-usd"
              dense
              v-model="totalPaid"
              :label="$t('components.claims_history.header.total_paid')"
              placeholder="0.00"
              class="money"
              clearable
              @input="onGlobalSearch"
            />
          </v-col>
          <v-col cols="auto" class="px-0 pt-4">
            <v-icon @click="selectTotalPaidRange = true">
              mdi-dots-horizontal
            </v-icon>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="pt-0 pt-2">
        <v-select
          v-model="selectedType"
          :menu-props="{ contentClass: 'text-left' }"
          :label="$t('views.coverage.submission_type')"
          name="submission_type"
          :items="['Predetermination', 'Regular Submission']"
          dense
          @input="onGlobalSearch"
          clearable
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="2" class="pt-0 pt-2">
        <v-select
          v-model="payee"
          :menu-props="{ contentClass: 'text-left' }"
          :label="$t('components.chequerequisitionform.payee')"
          name="payee"
          :items="['Member', 'Provider']"
          dense
          @input="onGlobalSearch"
          clearable
        ></v-select>
      </v-col>
    </v-row>
    <v-row class="search">
      <v-col cols="12" sm="6" md="2" class="pt-0 pt-2">
        <v-menu
          v-model="showDatepickerPayment"
          :nudge-right="40"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          left
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="paymentDateRangeText"
              :label="$t('components.claims_history.payment_date_range')"
              name="paymentDate"
              append-icon="mdi-calendar"
              dense
              readonly
              v-bind="attrs"
              v-on="on"
              clearable
              @click:clear="clearPaymentDates"
              @click:append="showDatepickerPayment = true"
            ></v-text-field>
          </template>
          <v-date-picker
            color="primary"
            @input="onPaymentDateSearch"
            no-title
            v-model="paymentDates"
            :max="$helpers.toISOStringLocal(new Date())"
            range
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="pt-0 pt-2">
        <v-text-field
          v-model="selectedCheque"
          :label="$t('views.planbalance.cheque_number')"
          name="cheque"
          dense
          append-icon="mdi-magnify"
          @input="onGlobalSearch"
          clearable
          :rules="[rules.paymentNumber]"
        ></v-text-field>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="pt-0 pt-2">
        <v-text-field
          v-model="batchNumber"
          :label="$t('headers.claims_payments.BatchNumber')"
          name="batch"
          dense
          append-icon="mdi-magnify"
          @input="onGlobalSearch"
          clearable
          :rules="[rules.batchNumber]"
        ></v-text-field>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-row no-gutters v-if="paymentAmountRange">
          <v-col class="pr-2">
            <v-range-slider
              color="accent"
              thumb-label="always"
              thumb-size="26"
              v-model="paymentRange"
              :max="max"
              :min="min"
              class="pt-3"
              @input="onGlobalSearch"
            />
          </v-col>
          <v-col cols="auto" class="px-0 pt-2">
            <v-icon small @click="disablePaymentRangeSelect">mdi-close</v-icon>
          </v-col>
        </v-row>
        <v-row no-gutters v-else>
          <v-col class="pr-0">
            <currency-textfield
              name="paymentAmount"
              prepend-inner-icon="mdi-currency-usd"
              dense
              v-model="paymentAmount"
              :label="
                $t('headers.employer_contributions_history.payment_amount')
              "
              placeholder="0.00"
              class="money"
              @input="onGlobalSearch"
              clearable
            />
          </v-col>
          <v-col cols="auto" class="px-0 pt-4">
            <v-icon @click="paymentAmountRange = true">
              mdi-dots-horizontal
            </v-icon>
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="auto" class="px-4 pt-4 text-right">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on" @click="resetForm">
              mdi-autorenew
            </v-icon>
          </template>
          <span>{{ $t(`globals.reset`) }}</span>
        </v-tooltip>
      </v-col>

      <!-- <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
          <currency-textfield
            name="claimedExpense"
            prepend-inner-icon="mdi-currency-usd"
            dense
            v-model="claimedExpense"
            v-on:keyup.enter="search"
            :label="$t('components.claims_history.header.claimed_expense')"
            placeholder="0.00"
          />
        </v-col> -->
    </v-row>
    <v-row class="search">
      <v-col cols="12" sm="6" md="2" class="py-0 pt-2">
        <v-select
          :menu-props="{ contentClass: 'text-left' }"
          dense
          v-model="selectedFlags"
          :items="flags"
          :item-text="(item) => trans(item, 'Name')"
          item-value="Id"
          multiple
          :label="$t('views.plangeneralparameters.flags')"
          @input="onGlobalSearch"
        ></v-select>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import CurrencyTextfield from "@/components/CurrencyTextfield.vue";
import { translate2 } from "@/trans2";
import TransEnum from "../TransEnum.vue";
import { mapActions, mapGetters } from "vuex";
export default {
  components: {
    CurrencyTextfield,
    TransEnum,
  },
  props: {
    loading: Boolean,
    reset: Boolean,
    certificateLength: String,
    planIdentityId: String,
    memberGroupIdentityIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      range: [0, 1200],
      totalPaidRange: [0, 1200],
      min: 0,
      max: 1200,
      selectAmountRange: false,
      selectTotalPaidRange: false,
      showDatepicker: false,
      valid: false,
      patient: null,
      claimedAmount: null,
      totalPaid: null,
      claimedExpense: null,
      dates: [],
      submissionId: null,
      selectedStatus: [],
      userType: null,
      selectedType: null,
      selectedCheque: null,
      batchNumber: null,
      payee: null,
      paymentAmount: null,
      paymentAmountRange: false,
      paymentRange: [0, 1200],
      selectedFlags: [],
      memberCertificate: null,
      rules: {
        claimSubmissionId: (v) =>
          !v ||
          /^(?:\d{14}|\d{3}-\d{14}|[A-Za-z]{3}-\d{3}-\d{14})$/.test(v) ||
          this.$t("validation.not_a_valid_claim_submission_id"),
        patient: (v) =>
          !v ||
          /^(([\da-zA-ZàâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ]+)([ '-])?)+([\da-zA-ZàâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ]+\.?)$/.test(
            v
          ) ||
          this.$t("validation.not_a_valid_patient_name"),
        memberCertificate: (v) => {
          if (
            this.certificateLength === null ||
            this.certificateLength === undefined
          ) {
            return true;
          }
          let length = this.certificateLength;
          let regex = new RegExp("^\\d{" + length + "}$");

          return (
            !v ||
            regex.test(v) ||
            this.$t("validation.not_a_valid_certificate_{0}", [length])
          );
        },
        paymentNumber: (v) =>
          !v ||
          /^[CD][0-9]{9}$/.test(v) ||
          this.$t("validation.not_a_valid_payment_number"),
        batchNumber: (v) =>
          !v ||
          /^\d{4}$/.test(v) ||
          this.$t("validation.not_a_valid_batch_number"),
      },
      selectedProviders: [],
      providerSearchMonitor: null,
      providerLoading: false,
      providerSearch: null,
      providers: [],
      providerSkip: 0,
      providerTop: 10,
      coverageSearch: null,
      coverageLoading: false,
      selectedCoverage: [],
      coverageSearchMonitor: null,
      coverage: [],
      coverageSkip: 0,
      coverageTop: 10,
      globalSearchMonitor: null,
      providerPreviousScrollTop: 0,
      totalProviders: 0,
      disableScrollEvent: false,
      providerPreviousDirectionDown: false,
      providerDirectionChanged: false,
      totalCoverage: 0,
      coverageDirectionChanged: false,
      coveragePreviousDirectionDown: false,
      coveragePreviousScrollTop: 0,
      showDatepickerPayment: false,
      paymentDates: []
    };
  },
  computed: {
    ...mapGetters("plansMembers", ["options"]),
    ...mapGetters("common", ["language", "settings"]),
    dateRangeText: {
      get() {
        return this.dates.join(" ~ ");
      },
      set(dates) {
        return dates;
      },
    },
    paymentsDateRangeText: {
      get() {
        return this.paymentDates.join(" ~ ");
      },
      set(dates) {
        return dates;
      },
    },
    // {
    //     New,
    //     Submitted,
    //     Approved,
    //     Rejected,
    //     Deleted,
    //     Pended,
    //     Issued,
    //     Deposited,
    //     Canceled
    // }
    statuses() {
      return [
        {
          text: this.$t("components.claims_history.statuses.new"),
          value: "New",
        },
        {
          text: this.$t("components.claims_history.statuses.submitted"),
          value: "Submitted",
        },
        {
          text: this.$t("components.claims_history.statuses.approved"),
          value: "Approved",
        },
        {
          text: this.$t("components.claims_history.statuses.rejected"),
          value: "Rejected",
        },
        {
          text: this.$t("components.claims_history.statuses.deleted"),
          value: "Deleted",
        },
        {
          text: this.$t("components.claims_history.statuses.pended"),
          value: "Pended",
        },
        {
          text: this.$t("components.claims_history.statuses.pending_payment"),
          value: "PendingPayment",
        },
        {
          text: this.$t("components.claims_history.header.paid"),
          value: "Paid",
        },
        {
          text: this.$t("globals.canceled"),
          value: "Canceled",
        },
        {
          text: this.$t("components.claims_history.statuses.archived"),
          value: "Archived",
        },
      ];
    },
    icon() {
      if (this.allItems) return "mdi-close-box";
      if (this.someItems) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    allItems() {
      return (
        this.selectedStatus &&
        this.selectedStatus.length === this.statuses.length
      );
    },
    someItems() {
      return (
        this.selectedStatus && this.selectedStatus.length > 0 && !this.allItems
      );
    },
    flags() {
      return [
        {
          Id: "no-flags",
          Name: "No Flags",
          Readonly: false,
          Translations: [
            {
              Name: "No Flags",
              LanguageCode: "en",
            },
          ],
        },
      ].concat(this.options.Models.ClaimFlag);
    },
    providerItemsWithSelected() {
      const selected = Array.isArray(this.selectedProviders)
        ? this.selectedProviders
        : this.selectedProviders
        ? [this.selectedProviders]
        : [];

      // Merge and deduplicate by Id
      const all = [...selected, ...this.providers];
      const seen = new Set();
      return all.filter((p) => {
        if (seen.has(p.Id)) return false;
        seen.add(p.Id);
        return true;
      });
    },
    coverageItemsWithSelected() {
      const selected = Array.isArray(this.selectedCoverage)
        ? this.selectedCoverage
        : this.selectedCoverage
        ? [this.selectedCoverage]
        : [];

      // Merge and deduplicate by Id
      const all = [...selected, ...this.coverage];
      const seen = new Set();
      return all.filter((p) => {
        if (seen.has(p.BenefitId)) return false;
        seen.add(p.BenefitId);
        return true;
      });
    },
  },
  methods: {
    ...mapActions("claims", [
      "searchProvidersForClaimSubmission",
      "searchPlanBenefits",
    ]),
    providerInput(v) {
      console.log("provider input: ", v);
    },
    providerChange(v) {
      console.log("provider change: ", v);
    },
    trans(item, field) {
      return translate2(item, field);
    },
    globalSearch() {
      console.log(this.selectedProviders, this.selectedCoverage);
      clearTimeout(this._timerId);
      this._timerId = setTimeout(async () => {
        this.$emit("search", {
          submissionId: this.submissionId,
          dates: this.dates,
          paymentDates: this.paymentDates,
          patient: this.patient,
          coverages: this.selectedCoverage,
          claimedAmount: this.claimedAmount,
          //   claimedExpense: this.claimedExpense,
          claimedAmountRange: this.range,
          selectAmountRange: this.selectAmountRange,
          totalPaid: this.totalPaid,
          selectTotalPaidRange: this.selectTotalPaidRange,
          totalPaidRange: this.totalPaidRange,
          status: this.selectedStatus,
          userType: this.userType,
          submissionType: this.selectedType,
          cheque: this.selectedCheque,
          batchNumber: this.batchNumber,
          payee: this.payee,
          paymentAmount: this.paymentAmount,
          paymentAmountRange: this.paymentRange,
          selectPaymentAmountRange: this.paymentAmountRange,
          selectedFlags: this.selectedFlags,
          providers: this.selectedProviders,
          memberCertificate: this.memberCertificate
        });
      }, 500);
    },
    async onProviderSearch(evt) {
      // console.log(evt.keyCode, evt.target.value);
      if (evt.keyCode >= 37 && evt.keyCode <= 40) {
        return;
      }
      if (evt.keyCode === 13) {
        return;
      }
      this.providerSkip = 0;
      const obj = {};
      this.providerSearchMonitor = obj;
      await this.delay(800);
      if (this.providerSearchMonitor !== obj) {
        return;
      }
      this.attachScrollListener(
        ".provider-autocomplete-menu",
        this.onProviderScroll
      );
      this.getProviders(evt.target.value);
      this.providerSearch = evt.target.value;
    },
    attachScrollListener(selector, listener, retries = 10) {
      console.log("attach scroll listener");
      // Use setTimeout to poll for the menu content
      setTimeout(() => {
        const menuContent = document.querySelector(selector);
        console.log("inside timeout");
        console.log(menuContent);
        if (menuContent) {
          menuContent.removeEventListener("scroll", listener); // remove existing
          menuContent.addEventListener("scroll", listener); // attach new
        } else if (retries > 0) {
          this.attachScrollListener(selector, listener, retries - 1); // retry until rendered
        }
      }, 100);
    },
    async onCoverageSearch(evt) {
      console.log(evt.keyCode, evt.target.value);
      if (evt.keyCode >= 37 && evt.keyCode <= 40) {
        return;
      }
      if (evt.keyCode === 13) {
        return;
      }
      this.coverageSkip = 0;
      const obj = {};
      this.coverageSearchMonitor = obj;
      await this.delay(800);
      if (this.coverageSearchMonitor !== obj) {
        return;
      }
      this.attachScrollListener(
        ".coverage-autocomplete-menu",
        this.onCoverageScroll
      );
      this.getCoverage(evt.target.value);
      this.coverageSearch = evt.target.value;
    },
    onDateOfExpenseSearch(dates) {
      if (dates.length === 2) {
        this.globalSearch();
      }
    },
    onPaymentDateSearch(dates) {
      if (dates.length === 2) {
        this.globalSearch();
      }
    },
    async onGlobalSearch() {
      this.valid = this.$refs.form.validate();
      console.log(this.valid);
      if (this.valid !== true) {
        return;
      }
      const obj = {};
      this.globalSearchMonitor = obj;
      await this.delay(800);
      if (this.globalSearchMonitor !== obj) {
        return;
      }
      this.globalSearch();
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async getProviders(search) {
      console.log("get providers: ", search);
      if (search === null || search === undefined || search === "") {
        return;
      }
      if (this.providerLoading) return;
      try {
        this.providerLoading = true;
        const response = await this.searchProvidersForClaimSubmission({
          submissionType: this.userType,
          coverageTypes: [
            ...new Set(this.selectedCoverage.map((o) => o.Benefit.Coverage)),
          ],
          search: search,
          skip: this.providerSkip,
          top: this.providerTop * 2,
        });
        console.log("#2");
        const newProviders = response.data.value || [];
        console.log("#3");
        this.totalProviders = response.data["@odata.count"];
        console.log("#4");

        this.providers = newProviders;
        this.providerSkip = this.providerTop;
        this.providerPreviousScrollTop = 0;
      } catch (error) {
        this.error = error;
      } finally {
        this.providerLoading = false;
      }
    },
    async scrollProviders(search) {
      console.log("scroll providers: ", search);
      if (search === null || search === undefined || search === "") {
        return this.providers;
      }
      if (this.providerLoading) return this.providers;
      try {
        this.providerLoading = true;
        const response = await this.searchProvidersForClaimSubmission({
          submissionType: this.userType,
          coverageTypes: [
            ...new Set(this.selectedCoverage.map((o) => o.Benefit.Coverage)),
          ],
          search: search,
          skip: this.providerSkip,
          top: this.providerTop,
        });

        return response.data.value || [];
      } catch (error) {
        this.error = error;
      } finally {
        this.providerLoading = false;
      }
      return this.providers;
    },
    async onProviderScroll(event) {
      console.log(
        "on provider scroll: ",
        event,
        this.providerSearch,
        this.providerLoading,
        this.disableScrollEvent
      );
      if (this.providerLoading || this.disableScrollEvent) return;
      this.disableScrollEvent = true;
      const el = event.target;
      console.log(
        "scrollTop: ",
        el.scrollTop,
        "clientHeight: ",
        el.clientHeight,
        "scrollHeight: ",
        el.scrollHeight,
        "providerTop: ",
        this.providerTop,
        "providerPreviousScrollTop: ",
        this.providerPreviousScrollTop
      );
      let directionDown = el.scrollTop > this.providerPreviousScrollTop;
      let directionUp = el.scrollTop < this.providerPreviousScrollTop;
      this.providerPreviousScrollTop = el.scrollTop;
      const directionChanged =
        directionDown && !this.providerPreviousDirectionDown;
      this.providerPreviousDirectionDown = directionDown;
      if (
        this.providerDirectionChanged === false &&
        directionChanged === true
      ) {
        this.providerDirectionChanged = true;
      }
      console.log(
        "directionDown: ",
        directionDown,
        "directionUp: ",
        directionUp,
        "directionChanged",
        directionChanged,
        "providerPreviousDirectionDown",
        this.providerPreviousDirectionDown,
        "this.directionChanged",
        this.providerDirectionChanged
      );

      const offset = 20;

      console.log(
        "distance from edge down: ",
        el.scrollTop + el.clientHeight,
        el.scrollHeight - offset
      );

      console.log("distance from edge up: ", el.scrollTop, offset);

      if (
        directionDown &&
        el.scrollTop + el.clientHeight >= el.scrollHeight - offset
      ) {
        console.log(
          "direction down scroll call",
          this.providerSkip,
          this.totalProviders
        );
        if (this.providerSkip === this.totalProviders) {
          this.disableScrollEvent = false;
          return;
        }
        // this.getProviders(this.providerSearch, true);
        this.providerSkip += this.providerTop;
        if (this.providerSkip > this.totalProviders - this.providerTop) {
          this.providerSkip = this.totalProviders - this.providerTop;
        }
        if (this.providerSkip < 20) {
          this.providerSkip = 20;
        }
        const newProviders = await this.scrollProviders(this.providerSearch);
        console.log("#3");
        let providers = [...this.providers, ...newProviders];
        if (providers.length > 2 * this.providerTop) {
          this.providers = providers.slice(this.providerTop);
        } else {
          this.providers = providers;
        }
        //set new scroll position in the middle of the list
        this.$nextTick(() => {
          // el.scrollTop = el.clientHeight / 2;
          el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
          this.providerPreviousScrollTop = el.scrollTop;
          this.$nextTick(() => {
            this.disableScrollEvent = false;
          });
        });
        return;
      }
      if (directionUp && el.scrollTop <= offset) {
        console.log("direction up scroll call", this.providerSkip);
        if (this.providerSkip === 0) {
          this.disableScrollEvent = false;
          return;
        }
        this.providerSkip -= this.providerTop;
        if (this.providerDirectionChanged) {
          this.providerSkip -= this.providerTop;
          this.providerDirectionChanged = false;
        }
        if (this.providerSkip < 0) {
          this.providerSkip = 0;
        }
        const oldProviders = await this.scrollProviders(this.providerSearch);
        let providers = [...oldProviders, ...this.providers];

        if (providers.length > 2 * this.providerTop) {
          this.providers = providers.slice(
            0,
            providers.length - this.providerTop
          );
        } else {
          this.providers = providers;
        }
        this.$nextTick(() => {
          // el.scrollTop = el.clientHeight / 2;
          el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
          this.providerPreviousScrollTop = el.scrollTop;
          this.$nextTick(() => {
            this.disableScrollEvent = false;
          });
        });
        return;
      }
      console.log("loaded providers: ", this.providers, this.providers.length);
      this.disableScrollEvent = false;
    },
    async onCoverageScroll(event) {
      console.log("on coverage scroll: ", event, this.coverageSearch);
      if (this.providerLoading || this.disableScrollEvent) return;
      this.disableScrollEvent = true;
      const el = event.target;
      let directionDown = el.scrollTop > this.coveragePreviousScrollTop;
      let directionUp = el.scrollTop < this.coveragePreviousScrollTop;
      this.coveragePreviousScrollTop = el.scrollTop;
      const directionChanged =
        directionDown && !this.coveragePreviousDirectionDown;
      this.coveragePreviousDirectionDown = directionDown;
      if (
        this.coverageDirectionChanged === false &&
        directionChanged === true
      ) {
        this.coverageDirectionChanged = true;
      }
      const offset = 20;
      if (
        directionDown &&
        el.scrollTop + el.clientHeight >= el.scrollHeight - offset
      ) {
        if (this.coverageSkip === this.totalCoverage) {
          this.disableScrollEvent = false;
          return;
        }
        // this.getProviders(this.providerSearch, true);
        this.coverageSkip += this.coverageTop;
        if (this.coverageSkip > this.totalCoverage - this.coverageTop) {
          this.coverageSkip = this.totalCoverage - this.coverageTop;
        }
        if (this.coverageSkip < 20) {
          this.coverageSkip = 20;
        }
        const newItems = await this.scrollCoverage(this.coverageSearch);

        let coverage = [...this.coverage, ...newItems];
        if (coverage.length > 2 * this.coverageTop) {
          this.coverage = coverage.slice(this.coverageTop);
        } else {
          this.coverage = coverage;
        }
        //set new scroll position in the middle of the list
        this.$nextTick(() => {
          // el.scrollTop = el.clientHeight / 2;
          el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
          this.providerPreviousScrollTop = el.scrollTop;
          this.$nextTick(() => {
            this.disableScrollEvent = false;
          });
        });
        return;
      }
      if (directionUp && el.scrollTop <= offset) {
        if (this.coverageSkip === 0) {
          this.disableScrollEvent = false;
          return;
        }
        this.coverageSkip -= this.coverageTop;
        if (this.coverageDirectionChanged) {
          this.coverageSkip -= this.coverageTop;
          this.coverageDirectionChanged = false;
        }
        if (this.coverageSkip < 0) {
          this.coverageSkip = 0;
        }
        const newItems = await this.scrollCoverage(this.coverageSearch);
        let coverage = [...newItems, ...this.coverage];

        if (coverage.length > 2 * this.coverageTop) {
          this.coverage = coverage.slice(0, coverage.length - this.coverageTop);
        } else {
          this.coverage = coverage;
        }
        this.$nextTick(() => {
          // el.scrollTop = el.clientHeight / 2;
          el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
          this.providerPreviousScrollTop = el.scrollTop;
          this.$nextTick(() => {
            this.disableScrollEvent = false;
          });
        });
        return;
      }
      this.disableScrollEvent = false;
    },
    async getCoverage(search) {
      console.log("coverage search 2: ", search);
      if (search === null || search === undefined || search === "") {
        return;
      }
      // Items have already been requested
      if (this.coverageLoading) return;
      try {
        this.coverageLoading = true;
        const response = await this.searchPlanBenefits({
          memberGroupIdentityIds: this.memberGroupIdentityIds,
          planIdentityId: this.planIdentityId,
          search: search,
          language: this.language.language,
          skip: this.coverageSkip,
          top: this.coverageTop * 2,
        });
        const newCoverage = response.data.value || [];
        this.totalCoverage = response.data["@odata.count"];

        this.coverage = newCoverage;
        this.coverageSkip += this.coverageTop;
      } catch (error) {
        this.error = error;
      } finally {
        this.coverageLoading = false;
      }
    },
    async scrollCoverage(search) {
      console.log("coverage search 2: ", search);
      if (search === null || search === undefined || search === "") {
        return;
      }
      // Items have already been requested
      if (this.coverageLoading) return this.coverage;
      try {
        this.coverageLoading = true;
        const response = await this.searchPlanBenefits({
          memberGroupIdentityIds: this.memberGroupIdentityIds,
          planIdentityId: this.planIdentityId,
          search: search,
          language: this.language.language,
          skip: this.coverageSkip,
          top: this.coverageTop,
        });
        return response.data.value || [];
      } catch (error) {
        this.error = error;
      } finally {
        this.coverageLoading = false;
      }
    },
    clearDates() {
      this.dates = [];
      this.onGlobalSearch();
    },
    clearPaymentDates() {
      this.paymentDates = [];
      this.onGlobalSearch();
    },
    disableRangeSelect() {
      this.selectAmountRange = false;
      this.onGlobalSearch();
    },
    disableTotalPaidRangeSelect() {
      this.selectTotalPaidRange = false;
      this.onGlobalSearch();
    },
    disablePaymentRangeSelect() {
      this.paymentAmountRange = false;
      this.onGlobalSearch();
    },
    resetForm() {
      this.submissionId = null;
      this.dates = [];
      this.paymentDates = [];
      this.patient = null;
      this.selectedCoverage = [];
      this.selectedProviders = [];
      this.claimedAmount = null;
      this.totalPaid = null;
      this.claimedExpense = null;
      this.selectAmountRange = false;
      this.selectTotalPaidRange = false;
      this.range = [0, 1200];
      this.paymentRange = [0, 1200];
      this.totalPaidRange = [0, 1200];
      this.selectedStatus = [];
      this.onGlobalSearch();
    },
    toggle() {
      this.$nextTick(() => {
        if (this.allItems) {
          this.change([]);
        } else {
          this.change(this.statuses.slice());
        }
      });
    },
    change($event) {
      // console.log($event);
      let items = $event.filter((o) => o !== null && o !== undefined);
      this.selectedStatus = items;
    },
  },
  mounted() {
    console.log("mounted: ", this.planIdentityId, this.certificateLength);
  },
  watch: {
    selectedProviders: {
      handler(v) {
        this.onGlobalSearch();
      },
    },
    selectedCoverage: {
      handler(v) {
        this.onGlobalSearch();
      },
    },
  },
};
</script>
<style scoped>
.search >>> input {
  font-size: 14px !important;
}
.search >>> .v-label {
  font-size: 14px !important;
  /* color: grey !important; */
}
.search >>> .v-select__selection {
  font-size: 14px !important;
}
.money >>> .v-input__slot {
  background-color: #eaeaea !important;
}
.search-form {
  background-color: #eaeaea;
}
.v-tooltip__content {
  max-width: 100px !important;
}
</style>
